// src/components/portal/PortalPeek.js — The portal's version of the CRM's
// Peek (src/components/Peek.js): click a contact or firm to see it in a
// floating window, with "Open full page" for its standalone page
// (/portal/contacts/:id, /portal/firms/:id). Links inside a peek stack, Back
// returns, Esc or a click outside closes.
//
// Same rule as portal search (Travis, 10/21): it shows nothing the partner
// can't already see. Everything here is built from the partner's own
// pipeline attribution (GET /api/portal/my-investors, the Contacts page's
// data). The CRM peek's internal fields (relationship owners, notes,
// enrichment, a firm's other people, activity) are never fetched.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../../lib/api'
import { PL_BADGE } from '../../lib/pipelineStages'

const PeekContext = createContext(null)

const FULL_PAGE = {
  contact: id => `/portal/contacts/${id}`,
  firm:    id => `/portal/firms/${id}`,
}

export function usePortalPeek() {
  return useContext(PeekContext)
}

// The partner's pipeline attribution, shared with the Contacts page.
export function usePortalDirectory() {
  return useQuery({
    queryKey: ['portalInvestors'],
    queryFn: () => api.get('/api/portal/my-investors').then(r => r.data.data ?? []),
  })
}

export function PortalPeekLink({ type, id, children, style }) {
  const peek = usePortalPeek()
  if (!peek || !id) return children
  return (
    <button type="button" onClick={e => { e.stopPropagation(); peek.open(type, id) }}
      style={{
        background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left',
        color: 'var(--blue-text)', cursor: 'pointer', ...style,
      }}>
      {children}
    </button>
  )
}

export function PortalPeekProvider({ children }) {
  const [stack, setStack] = useState([])
  const location = useLocation()

  const open = useCallback((type, id) => setStack(s => {
    const top = s[s.length - 1]
    if (top && top.type === type && top.id === id) return s
    return [...s, { type, id }]
  }), [])
  const back  = useCallback(() => setStack(s => s.slice(0, -1)), [])
  const close = useCallback(() => setStack([]), [])

  useEffect(() => { setStack([]) }, [location.pathname, location.search])

  const value = useMemo(() => ({ open, back, close }), [open, back, close])
  return (
    <PeekContext.Provider value={value}>
      {children}
      {stack.length > 0 && <PeekWindow stack={stack} onBack={back} onClose={close} />}
    </PeekContext.Provider>
  )
}

function PeekWindow({ stack, onBack, onClose }) {
  const navigate = useNavigate()
  const top = stack[stack.length - 1]

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const Body = { contact: ContactProfile, firm: FirmProfile }[top.type]

  return (
    <div onMouseDown={e => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div role="dialog" aria-modal="true" style={{
        background: 'var(--surface)', border: '1px solid var(--border-light)', borderRadius: 10,
        width: '100%', maxWidth: 640, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
      }}>
        <div style={{
          padding: '10px 16px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {stack.length > 1 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onBack} title="Back to the previous record">← Back</button>
          )}
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
            {top.type === 'contact' ? 'Contact' : 'Firm'}
          </span>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(FULL_PAGE[top.type](top.id))}>
            Open full page ↗
          </button>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close" style={{ fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '18px 22px', overflowY: 'auto' }}>
          <Body key={`${top.type}-${top.id}`} id={top.id} />
        </div>
      </div>
    </div>
  )
}

// ── Shared pieces ─────────────────────────────────────────────────────────

const list = v => [].concat(v ?? []).filter(x => x != null && x !== '')
const formatCurrency = n => (n == null ? null : `$${Number(n).toLocaleString()}`)
const formatDate = d => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : null)

function Loading() {
  return <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><div className="spinner" /></div>
}

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  )
}

function Fields({ rows }) {
  const shown = rows.filter(([, v]) => v != null && v !== '' && !(Array.isArray(v) && !v.length))
  if (!shown.length) return null
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 16px', fontSize: 13, marginTop: 14 }}>
      {shown.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <div style={{ color: 'var(--text-muted)' }}>{k}</div>
          <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{v}</div>
        </div>
      ))}
    </div>
  )
}

// One line per pipeline entry: mandate, stage, commitments, last activity.
function MandateRows({ rows, showContact }) {
  if (!rows.length) return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>None.</div>
  return rows.map(r => {
    const extra = [
      r.target_commitment != null && `Target ${formatCurrency(r.target_commitment)}`,
      r.actual_commitment != null && `Committed ${formatCurrency(r.actual_commitment)}`,
      r.last_activity && `Last activity ${formatDate(r.last_activity)}`,
    ].filter(Boolean).join(' · ')
    return (
      <div key={r.pipeline_id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, fontSize: 13, marginBottom: 6 }}>
        <Link to={`/portal/mandates/${r.mandate_id}`}>{r.mandate_name}</Link>
        <span className={`badge ${PL_BADGE[r.stage] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>{r.stage}</span>
        {showContact && r.contact_id && (
          <PortalPeekLink type="contact" id={r.contact_id}>{r.investor_contact_name}</PortalPeekLink>
        )}
        {extra && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{extra}</span>}
      </div>
    )
  })
}

function NotFound({ what }) {
  return <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>This {what} isn't among your contacts.</div>
}

// ── Contact ───────────────────────────────────────────────────────────────

export function ContactProfile({ id, large }) {
  const { data, isLoading } = usePortalDirectory()
  if (isLoading) return <Loading />
  const rows = (data ?? []).filter(r => r.contact_id === id)
  if (!rows.length) return <NotFound what="contact" />

  const c = rows[0]
  const firms = [...new Map(rows.filter(r => r.investor_firm_id)
    .map(r => [r.investor_firm_id, r.investor_firm_name])).entries()]

  return (
    <>
      <div style={{ fontSize: large ? 26 : 20, fontWeight: 700 }}>{c.investor_contact_name}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>
        {c.title_role && <div>{c.title_role}</div>}
        {firms.map(([fid, name]) => (
          <div key={fid}><PortalPeekLink type="firm" id={fid}>{name}</PortalPeekLink></div>
        ))}
      </div>
      <Fields rows={[
        ['Email', list(c.email).map(e => <div key={e}><a href={`mailto:${e}`} style={{ color: 'var(--accent)' }}>{e}</a></div>)],
        ['Phone', list(c.phone).join(', ')],
      ]} />
      <Section title="Your mandates with this contact">
        <MandateRows rows={rows} />
      </Section>
    </>
  )
}

// ── Firm ──────────────────────────────────────────────────────────────────

export function FirmProfile({ id, large }) {
  const { data, isLoading } = usePortalDirectory()
  if (isLoading) return <Loading />
  const rows = (data ?? []).filter(r => r.investor_firm_id === id)
  if (!rows.length) return <NotFound what="firm" />

  const people = [...new Map(rows.filter(r => r.contact_id)
    .map(r => [r.contact_id, r])).values()]

  return (
    <>
      <div style={{ fontSize: large ? 26 : 20, fontWeight: 700 }}>{rows[0].investor_firm_name}</div>
      <Section title={`Your contacts here (${people.length})`}>
        {!people.length ? <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No named contacts.</div>
          : people.map(p => (
            <div key={p.contact_id} style={{ fontSize: 13, marginBottom: 4 }}>
              <PortalPeekLink type="contact" id={p.contact_id}>{p.investor_contact_name}</PortalPeekLink>
              {p.title_role && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {p.title_role}</span>}
            </div>
          ))}
      </Section>
      <Section title="Your mandates with this firm">
        <MandateRows rows={rows} showContact />
      </Section>
    </>
  )
}
